import ecc from '@bitcoinerlab/secp256k1';
import { getBuffer } from '@dynamic-labs-sdk/client/core';
import type { Network } from 'bitcoinjs-lib';
import { Psbt } from 'bitcoinjs-lib';
import { ECPairFactory } from 'ecpair';

import { InsufficientFundsError } from '../../../errors/InsufficientFundsError';
import { InvalidAmountError } from '../../../errors/InvalidAmountError';
import { NoUTXOsFoundError } from '../../../errors/NoUTXOsFoundError';
import { DUST_LIMIT } from '../../constants';
import type { FeePriority } from '../../WaasBitcoinWalletProvider.types';
import { addInputsToPsbt } from '../addInputsToPsbt';
import { addOutputsToPsbt } from '../addOutputsToPsbt';
import { calculateFeeAndChange } from '../calculateFeeAndChange';
import { calculateUTXOTotal } from '../calculateUTXOTotal';
import { estimateTransactionFee } from '../estimateTransactionFee';
import { getUTXOs } from '../getUTXOs';
import { selectUTXOsLargestFirst } from '../selectUTXOsLargestFirst';
import { validateAndSelectUTXOs } from '../validateAndSelectUTXOs';
import { validateNotTaproot } from '../validateNotTaproot';

type BuildPsbtOptions = {
  accountAddress: string;
  amountInSatoshis: bigint;
  feePriority?: FeePriority;
  network: Network;
  publicKeyHex: string;
  recipientAddress: string;
};

/**
 * Builds a PSBT for a Bitcoin transaction with real UTXOs
 * Uses Largest-First UTXO selection strategy with accurate vSize fee estimation
 *
 * @param options.accountAddress - The sender's Bitcoin address
 * @param options.amountInSatoshis - Amount to send in satoshis
 * @param options.feePriority - Priority level for fee estimation ('low', 'medium', or 'high')
 * @param options.network - Bitcoin network configuration (mainnet or testnet)
 * @param options.publicKeyHex - The sender's public key in hexadecimal format
 * @param options.recipientAddress - The recipient's Bitcoin address
 * @returns A PSBT in Base64 format
 * @throws Error if insufficient funds, no UTXOs, or other errors
 */
export const buildPsbt = async ({
  accountAddress,
  amountInSatoshis,
  feePriority = 'medium',
  network,
  publicKeyHex,
  recipientAddress,
}: BuildPsbtOptions): Promise<string> => {
  if (amountInSatoshis <= BigInt(0)) {
    throw new InvalidAmountError({
      amountInSatoshis: Number(amountInSatoshis),
      reason: 'Amount must be greater than 0',
    });
  }

  validateNotTaproot(accountAddress);

  const allUTXOs = await getUTXOs(accountAddress);

  if (allUTXOs.length === 0) {
    throw new NoUTXOsFoundError({ address: accountAddress });
  }

  const publicKeyBuffer = new Uint8Array(getBuffer().from(publicKeyHex, 'hex'));
  const ECPair = ECPairFactory(ecc);

  // @ts-expect-error - ecpair types are incompatible with valibot >=1.2.0
  // The options are actually optional at runtime, but TypeScript infers them as required
  const publicKeyPair = ECPair.fromPublicKey(publicKeyBuffer, {
    compressed: true,
  });

  const amountInSatoshisNumber = Number(amountInSatoshis);

  const initialFeeEstimate = await estimateTransactionFee({
    feePriority,
    numInputs: 1,
    numOutputs: 1,
  });

  const targetAmount = amountInSatoshisNumber + initialFeeEstimate + DUST_LIMIT;

  let selectedUTXOs = selectUTXOsLargestFirst({
    targetAmount,
    utxos: allUTXOs,
  });
  const selectedTotal = calculateUTXOTotal(selectedUTXOs);

  selectedUTXOs = validateAndSelectUTXOs({
    allUTXOs,
    amountInSatoshis: amountInSatoshisNumber,
    feeEstimate: initialFeeEstimate,
    selectedTotal,
    selectedUTXOs,
  });

  const selectedTotalValue = calculateUTXOTotal(selectedUTXOs);

  const { changeAmountNumber, feeEstimate, hasChangeOutput } =
    await calculateFeeAndChange({
      amountInSatoshis,
      feePriority,
      selectedTotalValue,
      selectedUTXOs,
    });

  const maxToSpend = selectedTotalValue - feeEstimate;

  if (maxToSpend < amountInSatoshisNumber) {
    throw new InsufficientFundsError({
      availableSatoshis: maxToSpend,
      requiredSatoshis: amountInSatoshisNumber,
    });
  }

  const psbt = new Psbt({ network });

  addInputsToPsbt({
    network,
    psbt,
    publicKeyPair,
    selectedUTXOs,
  });

  addOutputsToPsbt({
    accountAddress,
    amountInSatoshis: amountInSatoshisNumber,
    changeAmount: changeAmountNumber,
    hasChangeOutput,
    network,
    psbt,
    recipientAddress,
  });

  return psbt.toBase64();
};
